import { ExternalLink, Loader2Icon, RotateCcw, ScanLine } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePholisScan } from "@/hooks/usePholisScan";
import { settings } from "@/settings";
import Tooltip from "../../../shared/Tooltip";
import { Badge } from "../../../ui/badge";

const DASHBOARD_UID = "pholis-variability";
const DASHBOARD_SLUG = "pholis-variability-chart";

/**
 * Builds the Grafana variability dashboard URL for a given lot.
 * Returns `null` if no base URL is configured.
 */
const buildDashboardUrl = (lotId: string): string | null => {
  if (!settings.pholisDashboardBaseUrl) {
    return null;
  }

  const url = new URL(`/d/${DASHBOARD_UID}/${DASHBOARD_SLUG}`, settings.pholisDashboardBaseUrl);
  url.searchParams.set("var-lot_id", lotId);
  return url.toString();
};

type PholisScanCardProps = {
  measurementRequestId: number;
  lotId: string;
};

export function PholisScanCard({ measurementRequestId, lotId }: PholisScanCardProps) {
  const {
    trigger: triggerScan,
    status,
    hasPreviousResults,
    isLoadingStatus,
  } = usePholisScan(measurementRequestId);
  const isScanning = status?.isRunning ?? false;
  const isEnqueued = status?.isEnqueued ?? false;
  const isFailed = status?.isFailed ?? false;
  const isBusy = isScanning || isEnqueued;

  const dashboardUrl = buildDashboardUrl(lotId);

  const onScanLot = async () => {
    await triggerScan(undefined, {
      onError: (err) => toast.error(`Error: ${err.message}`),
      onSuccess: () => {
        toast.success(`Started scan for lot "${lotId}".`);
      },
    });
  };

  const runButton = (
    <Button
      type="button"
      variant="outline"
      onClick={onScanLot}
      disabled={isBusy || isLoadingStatus}
      className="w-full"
    >
      {isLoadingStatus ? (
        <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
      ) : isFailed ? (
        <RotateCcw className="w-4 h-4 mr-2" />
      ) : (
        <ScanLine className="w-4 h-4 mr-2" />
      )}
      {isFailed ? "Retry analysis" : "Run new analysis"}
    </Button>
  );

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle>Pholis Scan</CardTitle>
        {isLoadingStatus ? (
          <Badge className="h-5 bg-gray-100 text-gray-600" variant="secondary">
            <Loader2Icon className="animate-spin" />
          </Badge>
        ) : isEnqueued ? (
          <Badge className="h-5 bg-blue-100 text-blue-700" variant="secondary">
            Enqueued
          </Badge>
        ) : isScanning ? (
          <Badge className="h-5 bg-blue-100 text-blue-700" variant="secondary">
            <Loader2Icon className="animate-spin" />
            Running
          </Badge>
        ) : isFailed ? (
          <Badge className="h-5 bg-red-100 text-red-700" variant="secondary">
            Failed
          </Badge>
        ) : (
          <Badge className="h-5 bg-gray-100 text-gray-600" variant="secondary">
            Ready
          </Badge>
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col items-center gap-4">
        <p className="text-sm text-muted-foreground">Lot ID: {lotId}</p>
        <div className="flex flex-1 w-full items-center justify-center text-center">
          {isLoadingStatus ? (
            <Loader2Icon className="w-5 h-5 animate-spin text-muted-foreground" />
          ) : hasPreviousResults ? (
            dashboardUrl && (
              <Button asChild variant="link" className="px-0">
                <a
                  href={dashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative inline-flex items-center pr-0"
                >
                  Latest scan results
                  <ExternalLink className="absolute left-full top-1/2 -translate-y-1/2 -ml-2 size-3!" />
                </a>
              </Button>
            )
          ) : isFailed ? (
            <div className="text-sm text-red-600">Last analysis failed</div>
          ) : (
            <div className="text-sm text-muted-foreground">No scan results available</div>
          )}
        </div>

        <div className="w-full">
          {isBusy ? (
            <Tooltip
              asChild
              content={isEnqueued ? "Scan enqueued" : "Analysis in progress"}
              side="bottom"
              trigger={<span className="block w-full cursor-not-allowed">{runButton}</span>}
            />
          ) : (
            runButton
          )}
        </div>
      </CardContent>
    </Card>
  );
}
